<template>
  <UISkeletonTree
    v-if="isSkeletonVisible"
    :message="skeletonMessage"
  />
  <AppTree
    v-else
    v-model:expanded-paths="expandedPaths"
    :current-asset
    :current-path
    :data
    :formatter
    :get-connector-info
    :get-description
    :repo-name
    placeholder="Search assets..."
    @click:connector="handleClickConnector"
    @click:menu="openEntity"
    @click:name="openEntity"
    @click:path="handleSetPath"
  />
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { get } from 'lodash'

import {
  AnalyticsPreparedTree,
  LinkedDataConnectorStatus,
} from '@types'

import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { UNKNOWN_VALUE } from '@/const/common'
import { ROUTE_NAME } from '@/const'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useSettingsStore } from '@/store/settings'

import RepositoryPopup from '@/modules/modals/Repositories/Data/RepositoryPopup.vue'
import { AppTree } from '@app'
import { UISkeletonTree } from '@ui/skeletons'

defineOptions({ name: 'MainTree' })

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const modalsStore = useModalsStore()
const repositoriesStore = useRepositoriesStore()
const settingsStore = useSettingsStore()

const route = useRoute()
const router = useRouter()

const expandedPaths = ref<Set<string>>(new Set())
const repositoryId = ref<string>()
const assetId = ref<string>()

const assetsBunch = computed(() => assetsBunchStore.getList)
const data = computed(() =>
  settingsStore.getOwnershipAdjustedValues
    ? analyticsStore.module.tree.getLookThroughPreparedData
    : analyticsStore.module.tree.getPreparedData,
)
const isLoading = computed(
  () =>
    !analyticsStore.getError &&
    (!analyticsStore.module.tree.isInit ||
      analyticsStore.module.tree.isLoading),
)
const isAssetsBunchInited = computed(() => assetsBunchStore.initFlag)

const isSkeletonVisible = computed(
  () => isLoading.value || !isAssetsBunchInited.value,
)

const repoName = computed(() => repositoriesStore.getCurrentName)

const currentAsset = computed(() => repositoryId.value || assetId.value)

const currentPath = computed(() => analyticsStore.getPath)

const skeletonMessage = computed(() =>
  isLoading.value ? 'Analytics calculating...' : 'Loading assets...',
)

const formatter = (key: string) => {
  const asset = assetsBunch.value.get(key)
  return asset?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
}

const getDescription = (key: string) => {
  const asset = assetsBunch.value.get(key)
  return asset?.field<string>(ASSET_FIELD.TICKER).value
}

const getConnectorInfo = (key: string) => {
  const asset = assetsBunch.value.get(key)
  if (!asset?.isLinked) return
  const connectorStatus = asset?.field<LinkedDataConnectorStatus>(ASSET_FIELD.CONNECTOR_STATUS).value
  const connectorStatusMessage = asset?.field<string>(ASSET_FIELD.CONNECTOR_STATUS_MESSAGE)
    .value
  switch (connectorStatus) {
    case LinkedDataConnectorStatus.ERROR:
    case LinkedDataConnectorStatus.LOGIN_REQUIRED:
      return {
        status: connectorStatus,
        message: connectorStatusMessage,
      }
    case LinkedDataConnectorStatus.WARNING:
      const linkedAccountStatus = asset?.field<LinkedDataConnectorStatus>(ASSET_FIELD.LINKED_ACCOUNT_STATUS).value
      if (!linkedAccountStatus) return {}
      const linkedAccountStatusMessage = asset?.field<string>(ASSET_FIELD.LINKED_ACCOUNT_STATUS_MESSAGE).value
      return {
        status: LinkedDataConnectorStatus.ERROR,
        message: linkedAccountStatusMessage,
      }
    default:
      return {}
  }
}

const handleClickConnector = (item: AnalyticsPreparedTree) => {
  assetId.value = item.path?.at(-1)
  if (!assetId.value) return
  const instance = assetsBunch.value.get(assetId.value)
  instance?.openConnectorInfo(assetId.value, () => {
    assetId.value = undefined
  })
}

const openEntity = (item: AnalyticsPreparedTree, isLeaf: boolean) => {
  if (item.path?.length === 1) {
    repositoryId.value = repositoriesStore.getCurrentId
    if (!repositoryId.value) return
    const modalInstance = modalsStore.init(
      `repository-${repositoryId.value}`,
      markRaw(RepositoryPopup),
    )

    modalInstance?.open(modalsStore.getZIndex(), {
      repository: repositoriesStore.getRepository,
    })
  } else {
    assetId.value = item.path?.at(-1)
    if (!assetId.value) return
    const instance = assetsBunch.value.get(assetId.value)
    const rawAmount = item.path
      ? get(analyticsStore.module.tree.getPreparedData, item.path)?.amount
      : item.amount
    instance?.openDrawer(
      item.path?.slice(1),
      rawAmount,
      () => {
        assetId.value = undefined
      },
      isLeaf
    )
  }
}

const handleSetPath = (item: AnalyticsPreparedTree, leaf: boolean) => {
  if (!item.path || leaf) return
  if (route.name !== ROUTE_NAME.DASHBOARDS_DASHBOARD) {
    router.push({ name: ROUTE_NAME.DASHBOARDS })
  }
  analyticsStore.setPath(item.path)
}

onMounted(() => {
  analyticsStore.setActive('Tree', ['tree'])
})

onUnmounted(() => {
  analyticsStore.setInActive('Tree')
})
</script>
